import React, { useCallback, useRef } from 'react';
import { X, Loader2, AlertCircle, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GoogleLoginButton } from '../buttons/google-login.button';
import FullLogo from '@/assets/images/logo/full_logo.svg';
import googleIconUrl from '@/assets/images/Google.png';
import appleIconUrl from '@/assets/images/apple.avif';
import WebBackground from '@/assets/images/bg-login-web.avif';
import { usePointerActivation } from '@/hooks';
import mixpanel from '@/configs/mixpanel';

interface AuthFormContentProps {
	onOpenChange?: (open: boolean) => void;
	email: string;
	setEmail: (email: string) => void;
	isLoading: boolean;
	handleSubmit: (e: React.FormEvent) => void;
	onGoogleMobileAuth: () => Promise<void>;
	onAppleMobileAuth: () => Promise<void>;
	onGoogleWebAuth: () => Promise<void>;
	onGoogleExternalBrowserHandoff: () => Promise<void>;
	onCopyExternalBrowserLink: () => Promise<void>;
	onGoogleWebAuthResponse: (response: any) => Promise<void>;
	onAppleWebAuth: () => Promise<void>;
	isWTN: boolean;
	isXInAppBrowser: boolean;
	xInAppBrowserPlatform: 'android' | 'ios' | 'unknown';
	showXBrowserHandoff: boolean;
	error?: string;
	setError: (error: string) => void;
	isPopUp?: boolean;
	autoFocusEmail?: boolean;
}

export function AuthFormContent({
	onOpenChange,
	email,
	setEmail,
	isLoading,
	handleSubmit,

	onGoogleMobileAuth,
	onAppleMobileAuth,
	onGoogleWebAuth,
	onGoogleExternalBrowserHandoff,
	onCopyExternalBrowserLink,
	onGoogleWebAuthResponse,
	onAppleWebAuth,
	isWTN,
	isXInAppBrowser,
	xInAppBrowserPlatform,
	showXBrowserHandoff,
	error,
	setError,
	isPopUp,
	autoFocusEmail = true,
}: AuthFormContentProps) {
	const handleClose = useCallback(() => {
		if (onOpenChange) onOpenChange(false);
	}, [onOpenChange]);

	const closePointerProps = usePointerActivation(handleClose);

	const emailInputRef = useRef<HTMLInputElement>(null);
	const emailPointerProps = usePointerActivation(() => {
		emailInputRef.current?.focus();
	});

	const trackAuthAttempted = useCallback((auth_method: string) => {
		mixpanel.track('[AUTH ATTEMPTED]', { auth_method });
		window.dataLayer?.push({ event: 'auth_attempted', auth_method });
	}, []);

	const googleMobilePointerProps = usePointerActivation(() => {
		trackAuthAttempted('google');
		if (isWTN) void onGoogleMobileAuth();
		else void onGoogleWebAuth();
	});
	const appleMobilePointerProps = usePointerActivation(() => {
		trackAuthAttempted('apple');
		void onAppleMobileAuth();
	});

	const handleAppleWebClick = useCallback(() => {
		trackAuthAttempted('apple');
		void onAppleWebAuth();
	}, [onAppleWebAuth, trackAuthAttempted]);

	const handleXGoogleClick = useCallback(() => {
		void onGoogleExternalBrowserHandoff();
	}, [onGoogleExternalBrowserHandoff]);

	const handleCopyBrowserLink = useCallback(() => {
		void onCopyExternalBrowserLink();
	}, [onCopyExternalBrowserLink]);

	const onFormSubmit = useCallback(
		(e: React.FormEvent) => {
			trackAuthAttempted('email');
			handleSubmit(e);
		},
		[handleSubmit, trackAuthAttempted]
	);

	return (
		<>
			{!isPopUp && (
				<div className="absolute inset-0 z-0 rounded-t-[38px] overflow-hidden pointer-events-none h-[250px]">
					<img
						src={WebBackground}
						className="w-full h-full object-cover opacity-20"
						loading="lazy"
						alt="background"
					/>
					<div className="absolute inset-0 bg-gradient-to-b from-[#101423]/40 to-[#101423]" />
				</div>
			)}

			<div className="relative z-10 w-full h-full flex flex-col items-center">
				{onOpenChange && (
					<button
						{...closePointerProps}
						className="absolute right-0 top-0 p-2.5 text-[#F7F7F8]/60 hover:text-[#F7F7F8] bg-white/15 rounded-full transition-colors z-20"
					>
						<X className="w-5 h-5" strokeWidth={1} />
					</button>
				)}

				<div className="w-full max-w-[420px]">
					<div className="mt-8 mb-6 [@media(max-height:600px)]:mt-4 [@media(max-height:600px)]:mb-3 flex justify-center">
						<img
							src={FullLogo}
							alt="Sports Predict"
							className="w-[122px] h-[64px] object-contain"
						/>
					</div>

					<h2
						className={`text-center text-[#F7F7F8] text-[20px] font-medium leading-tight max-w-[260px] mx-auto ${isPopUp ? 'mb-6' : 'mb-8 [@media(max-height:600px)]:mb-4'} orbitron`}
					>
						<span className="text-[#FBB03B]">#1</span>{' '}
						<span className="text-[#81A1F8]">Free-to-Play</span> Sports Prediction Platform
					</h2>

					<div className="w-full text-center mb-6 [@media(max-height:600px)]:mb-3">
						<h3 className="text-[#F7F7F8] text-[20px] font-semibold">Log in or Sign up</h3>
					</div>

					{error && (
						<div className="w-full mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400 text-sm">
							<AlertCircle className="w-4 h-4" />
							<p>{error}</p>
						</div>
					)}

					<form
						onSubmit={onFormSubmit}
						className="w-full space-y-2.5 mb-7 [@media(max-height:600px)]:mb-4"
					>
						<div className="relative">
							<Input
								ref={emailInputRef}
								{...emailPointerProps}
								type="email"
								placeholder="Type your email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="w-full h-11 bg-[#1E2748]/80 border-[#506296] rounded-full px-5 text-[#F7F7F8] placeholder:text-[#B5B6BA] focus:border-[#2667FF] focus:ring-1 focus:ring-[#2667FF] transition-all backdrop-blur-sm"
								autoFocus={autoFocusEmail}
								required
							/>
						</div>

						<Button
							type="submit"
							disabled={isLoading}
							className="w-full h-11 bg-[#2667FF] hover:bg-[#2667FF]/90 text-white font-bold rounded-full transition-all shadow-[0_0_20px_rgba(38,103,255,0.3)] hover:shadow-[0_0_30px_rgba(38,103,255,0.65)]"
						>
							{isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit'}
						</Button>
					</form>

					<div className="w-full flex flex-col items-center gap-4 mb-8 [@media(max-height:600px)]:mb-4">
						{isWTN ? (
							<div className="w-full space-y-3">
								<button
									type="button"
									{...googleMobilePointerProps}
									disabled={isLoading}
									className="w-full h-11 bg-transparent border border-[#CFDBEC] hover:bg-white/10 rounded-full flex items-center justify-center gap-3 transition-all group backdrop-blur-sm"
								>
									<img src={googleIconUrl} alt="Google" className="w-5 h-5" />
									<span className="text-[#F7F7F8] text-sm font-bold">Continue with Google</span>
								</button>

								<button
									type="button"
									{...appleMobilePointerProps}
									disabled={isLoading}
									className="w-full h-11 bg-transparent border border-[#CFDBEC] hover:bg-white/10 rounded-full flex items-center justify-center gap-3 transition-all group backdrop-blur-sm"
								>
									<img
										src={appleIconUrl}
										alt="Apple"
										className="w-5 h-5 invert group-hover:invert-0 transition-all"
									/>
									<span className="text-[#F7F7F8] text-sm font-bold">Continue with Apple</span>
								</button>
							</div>
						) : (
							<div className="flex flex-col items-center w-full space-y-3">
								{isXInAppBrowser ? (
									<div className="w-full space-y-3">
										<button
											type="button"
											onClick={handleXGoogleClick}
											disabled={isLoading}
											className="w-full h-11 bg-white text-[#1F2937] border border-[#CFDBEC] hover:bg-white/90 rounded-full flex items-center justify-center gap-3 transition-all group backdrop-blur-sm font-bold text-sm"
										>
											<img src={googleIconUrl} alt="Google" className="w-5 h-5" />
											<span>Continue with Google</span>
											<ExternalLink className="w-4 h-4 text-[#5F6063]" />
										</button>

										{showXBrowserHandoff && (
											<div className="rounded-2xl border border-[#506296] bg-[#1E2748]/80 p-3 text-left text-[#F7F7F8]">
												<p className="text-sm font-semibold">Open in browser to continue</p>
												<p className="mt-1 text-xs leading-4 text-[#B5B6BA]">
													Google sign-in cannot open inside X.
													{xInAppBrowserPlatform === 'android'
														? ' If Chrome did not open, copy this link and open it in your browser.'
														: ' Tap the X menu and choose Open in browser, or copy this link into Safari.'}
												</p>
												<button
													type="button"
													onClick={handleCopyBrowserLink}
													className="mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-full border border-[#CFDBEC] text-sm font-bold text-[#F7F7F8] transition-all hover:bg-white/10"
												>
													<Copy className="h-4 w-4" />
													Copy Link
												</button>
											</div>
										)}
									</div>
								) : (
									<div onClickCapture={() => trackAuthAttempted('google')} className="w-full">
										<GoogleLoginButton
											onSuccess={onGoogleWebAuthResponse}
											onError={() => setError('Google login failed. Please try again.')}
										/>
									</div>
								)}
								<button
									type="button"
									onClick={handleAppleWebClick}
									disabled={isLoading}
									className="w-full h-11 bg-transparent border border-[#CFDBEC] hover:bg-white/10 rounded-full flex items-center justify-center gap-3 transition-all group backdrop-blur-sm"
								>
									<img
										src={appleIconUrl}
										alt="Apple"
										className="w-5 h-5 invert group-hover:invert-0 transition-all"
									/>
									<span className="text-[#F7F7F8] text-sm font-bold">Continue with Apple</span>
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
}
